import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { sendContactForm } from '../utils/contactService';

const WorkTogether = () => {
  const { t } = useTranslation();
  
  // Add new state variables for form submission
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionStatus, setSubmissionStatus] = useState(null); // 'success', 'error', or null
  
  const [formData, setFormData] = useState({
    name: '',
    companyName: '',
    email: '',
    mobilePhone: '',
    idea: '',
    budget: '',
    deliveryTime: '',
    customService: '',
    services: {
      webDevelopment: false,
      softwareAppDevelopment: false,
      designBranding: false,
      marketingAdvertising: false,
      aiProcessOptimization: false
    },
    termsAgreed: false
  });
  
  const [dropdownStates, setDropdownStates] = useState({
    budget: false,
    deliveryTime: false
  });
  
  const [hoveredButtons, setHoveredButtons] = useState({
    webDevelopment: false,
    softwareAppDevelopment: false,
    designBranding: false,
    marketingAdvertising: false,
    aiProcessOptimization: false
  });
  
  const [customServiceButtonHovered, setCustomServiceButtonHovered] = useState(false);
  const [sendButtonHovered, setSendButtonHovered] = useState(false);
  const [showCustomServiceInput, setShowCustomServiceInput] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [showErrors, setShowErrors] = useState(false);

  // Add resetForm function
  const resetForm = () => {
    setFormData({
      name: '',
      companyName: '',
      email: '',
      mobilePhone: '',
      idea: '',
      budget: '',
      deliveryTime: '',
      customService: '',
      services: {
        webDevelopment: false,
        softwareAppDevelopment: false,
        designBranding: false,
        marketingAdvertising: false,
        aiProcessOptimization: false
      },
      termsAgreed: false
    });
    setShowCustomServiceInput(false);
  };

  // Handle initial touch
  useEffect(() => {
    // Function to clear all hover effects
    const clearAllHoverEffects = () => {
      setHoveredButtons({
        webDevelopment: false,
        softwareAppDevelopment: false,
        designBranding: false,
        marketingAdvertising: false,
        aiProcessOptimization: false
      });
      // Also clear custom service button hover
      setCustomServiceButtonHovered(false);
    };

    // Add event listener for any touch on the document
    document.addEventListener('touchstart', clearAllHoverEffects);
    
    // Cleanup
    return () => {
      document.removeEventListener('touchstart', clearAllHoverEffects);
    };
  }, []);

  const handleButtonHover = (buttonId, isHovering) => {
    setHoveredButtons({
      ...hoveredButtons,
      [buttonId]: isHovering
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleServiceToggle = (service) => {
    setFormData({
      ...formData,
      services: {
        ...formData.services,
        [service]: !formData.services[service]
      }
    });
  };

  const handleTermsToggle = () => {
    setFormData({
      ...formData,
      termsAgreed: !formData.termsAgreed
    });
  };
  
  const toggleDropdown = (dropdown) => {
    // Close any other open dropdowns when opening a new one
    const newState = {
      budget: dropdown === 'budget' ? !dropdownStates.budget : false,
      deliveryTime: dropdown === 'deliveryTime' ? !dropdownStates.deliveryTime : false
    };
    
    setDropdownStates(newState);
  };
  
  const handleOptionSelect = (dropdown, value) => {
    setFormData({
      ...formData,
      [dropdown]: value
    });
    setDropdownStates({
      ...dropdownStates,
      [dropdown]: false
    });
  };

  const toggleCustomServiceInput = () => {
    setShowCustomServiceInput(!showCustomServiceInput);
    // Reset hover state when toggling
    setCustomServiceButtonHovered(false);
  };

  // Form validation
  const validateForm = () => {
    const errors = {};
    
    // Required fields
    if (!formData.name.trim()) errors.name = t('error_name_required');
    if (!formData.email.trim()) errors.email = t('error_email_required');
    if (!formData.mobilePhone.trim()) errors.mobilePhone = t('error_phone_required');
    // Idea field is now optional
    if (!formData.budget) errors.budget = t('error_budget_required');
    if (!formData.deliveryTime) errors.deliveryTime = t('error_delivery_time_required');
    
    // Check if at least one service is selected or custom service is provided
    const hasSelectedService = Object.values(formData.services).some(value => value === true) || 
                             (showCustomServiceInput && formData.customService.trim() !== '');
                             
    if (!hasSelectedService) errors.services = t('error_service_required');
    
    // Check terms agreement
    if (!formData.termsAgreed) errors.termsAgreed = t('error_terms_required');
    
    return errors;
  };

  // Updated handleSubmit to use Firebase functions
  const handleSubmit = async () => {
    // Validate form
    const errors = validateForm();
    
    if (Object.keys(errors).length === 0) {
      // Set submitting state
      setIsSubmitting(true);
      setSubmissionStatus(null);
      
      try {
        // Send the form data using our contactService
        const response = await sendContactForm(formData);
        console.log('Form submission response:', response);
        
        // Show success message and reset form
        setSubmissionStatus('success');
        resetForm();
        
        // Hide success message after 5 seconds
        setTimeout(() => {
          setSubmissionStatus(null);
        }, 5000);
      } catch (error) {
        console.error('Error submitting form:', error);
        setSubmissionStatus('error');
      } finally {
        setIsSubmitting(false);
      }
    } else {
      // Show errors
      setFormErrors(errors);
      setShowErrors(true);
      
      // Scroll to first error
      const firstErrorField = Object.keys(errors)[0];
      const element = document.getElementById(firstErrorField);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  };

  // Success and Error message components
  const ThankYouMessage = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-green-900 text-white p-8 rounded-lg max-w-md text-center">
        <h2 className="text-2xl font-bold mb-4">{t('thank_you_heading')}</h2>
        <p className="mb-6">{t('thank_you_message')}</p>
        <button 
          onClick={() => setSubmissionStatus(null)} 
          className="bg-white text-green-900 px-6 py-2 rounded font-bold hover:bg-opacity-90 transition-all"
        >
          {t('close')}
        </button>
      </div>
    </div>
  );

  const ErrorMessage = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-red-600 text-white p-8 rounded-lg max-w-md text-center">
        <h2 className="text-2xl font-bold mb-4">{t('error_heading')}</h2>
        <p className="mb-6">{t('error_message')}</p>
        <button 
          onClick={() => setSubmissionStatus(null)} 
          className="bg-white text-red-600 px-6 py-2 rounded font-bold hover:bg-opacity-90 transition-all"
        >
          {t('try_again')}
        </button>
      </div>
    </div>
  );

  // Service data with translation keys
  const serviceItems = [
    { id: 'webDevelopment', textKey: 'service_web_development' },
    { id: 'softwareAppDevelopment', textKey: 'service_software_app' },
    { id: 'designBranding', textKey: 'service_design_branding' },
    { id: 'marketingAdvertising', textKey: 'service_marketing' },
    { id: 'aiProcessOptimization', textKey: 'service_ai_optimization' }
  ];

  // Budget options with translation keys
  const budgetOptions = [
    { value: '€1,000 - €5,000', textKey: 'budget_1k_5k' },
    { value: '€5,000 - €10,000', textKey: 'budget_5k_10k' },
    { value: '€10,000 - €25,000', textKey: 'budget_10k_25k' },
    { value: '€25,000+', textKey: 'budget_25k_plus' }
  ];

  // Delivery time options with translation keys
  const deliveryTimeOptions = [
    { value: t('delivery_1_month'), textKey: 'delivery_1_month' },
    { value: t('delivery_2_3_months'), textKey: 'delivery_2_3_months' },
    { value: t('delivery_3_6_months'), textKey: 'delivery_3_6_months' },
    { value: t('delivery_6_plus_months'), textKey: 'delivery_6_plus_months' }
  ];

  return (
    <>
      {/* Main background container */}
      <div className="text-left min-h-screen py-6 px-3 md:py-10 md:px-4 lg:py-16 lg:px-10">
        {/* Green form container with margins on sides */}
        <div className="bg-green-900 work-together-bg text-white max-w-7xl mx-auto rounded-lg overflow-hidden">
          <div className="p-4 sm:p-6 md:p-8 lg:p-12">
            <div className="w-full max-w-6xl mx-auto">
              <div className="flex justify-center items-center mb-8 md:mb-12 lg:mb-16">
                <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-6xl font-bold">{t('work')}</h1>
                <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-6xl font-bold ml-3 md:ml-6">{t('together')}</h1>
                <span className="text-3xl sm:text-4xl md:text-5xl lg:text-7xl ml-1 md:ml-2 text-black">?</span>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 lg:gap-x-16 gap-y-6 md:gap-y-4">
                {/* Left Column */}
                <div className="space-y-6 md:space-y-8">
                  <div>
                    <label htmlFor="name" className="block text-base sm:text-lg md:text-xl mb-1 md:mb-2">{t('your_name')}</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className={`w-full bg-transparent border-b-2 ${formErrors.name && showErrors ? 'border-red-500' : 'border-white/50'} py-1 md:py-2 focus:outline-none focus:border-white text-sm md:text-base`}
                    />
                    {formErrors.name && showErrors && (
                      <p className="text-red-500 text-xs md:text-sm mt-1">{formErrors.name}</p>
                    )}
                  </div>
                  
                  <div>
                    <label htmlFor="companyName" className="block text-base sm:text-lg md:text-xl mb-1 md:mb-2">{t('your_company_name')}</label>
                    <input
                      type="text"
                      id="companyName"
                      name="companyName"
                      value={formData.companyName}
                      onChange={handleInputChange}
                      className="w-full bg-transparent border-b-2 border-white/50 py-1 md:py-2 focus:outline-none focus:border-white text-sm md:text-base"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="email" className="block text-base sm:text-lg md:text-xl mb-1 md:mb-2">{t('your_email')}</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className={`w-full bg-transparent border-b-2 ${formErrors.email && showErrors ? 'border-red-500' : 'border-white/50'} py-1 md:py-2 focus:outline-none focus:border-white text-sm md:text-base`}
                    />
                    {formErrors.email && showErrors && (
                      <p className="text-red-500 text-xs md:text-sm mt-1">{formErrors.email}</p>
                    )}
                  </div>
                  
                  <div>
                    <label htmlFor="mobilePhone" className="block text-base sm:text-lg md:text-xl mb-1 md:mb-2">{t('your_mobile_phone')}</label>
                    <input
                      type="tel"
                      id="mobilePhone"
                      name="mobilePhone"
                      value={formData.mobilePhone}
                      onChange={handleInputChange}
                      className={`w-full bg-transparent border-b-2 ${formErrors.mobilePhone && showErrors ? 'border-red-500' : 'border-white/50'} py-1 md:py-2 focus:outline-none focus:border-white text-sm md:text-base`}
                    />
                    {formErrors.mobilePhone && showErrors && (
                      <p className="text-red-500 text-xs md:text-sm mt-1">{formErrors.mobilePhone}</p>
                    )}
                  </div>
                  
                  <div>
                    <label htmlFor="idea" className="block text-base sm:text-lg md:text-xl mb-1 md:mb-2">{t('your_idea')}</label>
                    <input
                      type="text"
                      id="idea"
                      name="idea"
                      value={formData.idea}
                      onChange={handleInputChange}
                      className="w-full bg-transparent border-b-2 border-white/50 py-1 md:py-2 focus:outline-none focus:border-white text-sm md:text-base"
                    />
                  </div>
                </div>
                
                {/* Right Column */}
                <div className="space-y-6 md:space-y-8">
                  <div>
                    <label className="block text-base sm:text-lg md:text-xl mb-2 md:mb-4">{t('project_budget')}</label>
                    <div className="relative">
                      {/* Container div that will have the error styles */}
                      <div className={`relative ${formErrors.budget && showErrors ? 'ring-2 ring-red-500' : ''}`}>
                        <button 
                          onClick={() => toggleDropdown('budget')}
                          className="w-full bg-white text-black py-2 md:py-3 px-3 md:px-4 text-left flex justify-between items-center text-sm md:text-base"
                          id="budget"
                        >
                          <span className="block pr-12 md:pr-16 overflow-hidden whitespace-nowrap text-ellipsis">
                            {formData.budget ? formData.budget : t('choose_budget')}
                          </span>
                          {/* Fixed width container for arrow */}
                          <div className="h-full w-8 md:w-12 bg-black text-white flex items-center justify-center absolute right-0 top-0 bottom-0">
                            <svg 
                              className={`w-4 h-4 md:w-6 md:h-6 transition-transform duration-300 ${dropdownStates.budget ? 'rotate-180' : ''}`} 
                              fill="none" 
                              viewBox="0 0 24 24" 
                              stroke="currentColor"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          </div>
                        </button>
                      </div>
                      
                      {/* Error message outside the button container */}
                      {formErrors.budget && showErrors && (
                        <p className="text-red-500 text-xs md:text-sm mt-1">{formErrors.budget}</p>
                      )}
                      
                      {dropdownStates.budget && (
                        <div className="absolute w-full bg-white text-black mt-1 z-20 shadow-lg">
                          {budgetOptions.map((option, index) => (
                            <div 
                              key={index}
                              className="py-2 px-3 md:px-4 hover:bg-gray-100 cursor-pointer text-sm md:text-base"
                              onClick={() => handleOptionSelect('budget', option.value)}
                            >
                              {option.value}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-base sm:text-lg md:text-xl mb-2 md:mb-4">{t('which_services')}</label>
                    <div id="services" className={`flex flex-wrap gap-2 md:gap-4 ${formErrors.services && showErrors ? 'p-2 ring-2 ring-red-500 rounded' : ''}`}>
                      {formErrors.services && showErrors && (
                        <p className="text-red-500 text-xs md:text-sm w-full mb-2">{formErrors.services}</p>
                      )}
                      
                      {/* Service toggle buttons */}
                      {serviceItems.map((service) => (
                        <div 
                          key={service.id}
                          onClick={() => {
                            handleServiceToggle(service.id);
                            // Clear all hover states on click with a timeout to ensure proper rendering
                            setTimeout(() => {
                              setHoveredButtons({
                                webDevelopment: false,
                                softwareAppDevelopment: false,
                                designBranding: false,
                                marketingAdvertising: false,
                                aiProcessOptimization: false
                              });
                            }, 50);
                          }}
                          onMouseEnter={() => handleButtonHover(service.id, true)}
                          onMouseLeave={() => handleButtonHover(service.id, false)}
                          onTouchStart={() => {
                            // Immediately clear any hover effects on touch
                            setHoveredButtons({
                              webDevelopment: false,
                              softwareAppDevelopment: false,
                              designBranding: false,
                              marketingAdvertising: false,
                              aiProcessOptimization: false
                            });
                          }}
                          className="relative cursor-pointer py-2 md:py-3 overflow-hidden text-xs sm:text-sm md:text-base"
                          style={{ 
                            display: 'inline-block',
                            width: 'auto',
                            border: '2px solid white',
                          }}
                        >
                          {/* White background that expands slightly on hover */}
                          <div
                            className="absolute top-0 left-0 h-full bg-white transition-all duration-300 ease-in-out"
                            style={{
                              width: formData.services[service.id]
                                ? '100%'
                                : hoveredButtons[service.id]
                                  ? '30%'
                                  : '0%'
                            }}
                          />
                          {/* Text */}
                          <div className="relative z-10 whitespace-nowrap px-3 md:px-6 text-white">
                            {t(service.textKey)}
                          </div>

                          {/* Black text that appears with overlay */}
                          <div
                            className="absolute top-0 left-0 h-full flex items-center overflow-hidden z-10 pointer-events-none transition-all duration-300 ease-in-out"
                            style={{
                              width: formData.services[service.id]
                                ? '100%'
                                : hoveredButtons[service.id]
                                  ? '30%'
                                  : '0%'
                            }}
                          >
                            <div className="px-3 md:px-6 text-black whitespace-nowrap">
                              {t(service.textKey)}
                            </div>
                          </div>
                        </div>
                      ))}
                      
                      {/* Custom service button with + sign - with same hover effect as service buttons */}
                      <div 
                        onClick={() => {
                          toggleCustomServiceInput();
                          // Clear hover state with timeout
                          setTimeout(() => {
                            setCustomServiceButtonHovered(false);
                          }, 50);
                        }}
                        onMouseEnter={() => setCustomServiceButtonHovered(true)}
                        onMouseLeave={() => setCustomServiceButtonHovered(false)}
                        onTouchStart={() => setCustomServiceButtonHovered(false)}
                        className="relative cursor-pointer overflow-hidden flex items-center justify-center text-xs sm:text-sm md:text-base"
                        style={{ 
                          width: '44px',
                          height: '44px',
                          border: '2px solid white',
                          borderRadius: '4px'
                        }}
                      >
                        {/* White background that expands from left */}
                        <div
                          className="absolute top-0 left-0 h-full bg-white transition-all duration-300 ease-in-out"
                          style={{
                            width: showCustomServiceInput
                              ? '100%'
                              : customServiceButtonHovered
                                ? '30%'
                                : '0%'
                          }}
                        />

                        {/* Plus sign */}
                        <div className="relative z-10 whitespace-nowrap text-white text-xl font-bold">
                          +
                        </div>
                        <div
                          className="absolute top-0 left-0 h-full flex items-center justify-center overflow-hidden z-10 pointer-events-none transition-all duration-300 ease-in-out"
                          style={{
                            width: showCustomServiceInput
                              ? '100%'
                              : customServiceButtonHovered
                                ? '30%'
                                : '0%'
                          }}
                        >
                          <div className="whitespace-nowrap text-black text-xl font-bold">+</div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Custom service text input when expanded */}
                    {showCustomServiceInput && (
                      <div className="mt-3">
                        <input
                          type="text"
                          id="customService"
                          name="customService"
                          value={formData.customService}
                          onChange={handleInputChange}
                          placeholder={t('custom_service_placeholder')}
                          className="w-full bg-transparent border-b-2 border-white/50 py-1 md:py-2 focus:outline-none focus:border-white text-sm md:text-base"
                        />
                      </div>
                    )}
                  </div>
                  
                  <div>
                    <label className="block text-base sm:text-lg md:text-xl mb-2 md:mb-4">{t('delivery_time')}</label>
                    <div className="relative">
                      {/* Container div that will have the error styles */}
                      <div className={`relative ${formErrors.deliveryTime && showErrors ? 'ring-2 ring-red-500' : ''}`}>
                        <button 
                          onClick={() => toggleDropdown('deliveryTime')}
                          className="w-full bg-white text-black py-2 md:py-3 px-3 md:px-4 text-left flex justify-between items-center text-sm md:text-base"
                          id="deliveryTime"
                        >
                          <span className="block pr-12 md:pr-16 overflow-hidden whitespace-nowrap text-ellipsis">
                            {formData.deliveryTime ? formData.deliveryTime : t('choose_delivery_time')}
                          </span>
                          {/* Fixed width container for arrow */}
                          <div className="h-full w-8 md:w-12 bg-black text-white flex items-center justify-center absolute right-0 top-0 bottom-0">
                            <svg 
                              className={`w-4 h-4 md:w-6 md:h-6 transition-transform duration-300 ${dropdownStates.deliveryTime ? 'rotate-180' : ''}`} 
                              fill="none" 
                              viewBox="0 0 24 24" 
                              stroke="currentColor"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          </div>
                        </button>
                      </div>
                      
                      {/* Error message outside the button container */}
                      {formErrors.deliveryTime && showErrors && (
                        <p className="text-red-500 text-xs md:text-sm mt-1">{formErrors.deliveryTime}</p>
                      )}
                      
                      {dropdownStates.deliveryTime && (
                        <div className="absolute w-full bg-white text-black mt-1 z-20 shadow-lg">
                          {deliveryTimeOptions.map((option, index) => (
                            <div 
                              key={index}
                              className="py-2 px-3 md:px-4 hover:bg-gray-100 cursor-pointer text-sm md:text-base"
                              onClick={() => handleOptionSelect('deliveryTime', option.value)}
                            >
                              {t(option.textKey)}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                
                {/* Full Width Elements */}
                <div className="md:col-span-2 flex flex-col md:flex-row items-start md:items-center justify-between mt-8 md:mt-12 lg:mt-16">
                  <div className="flex items-start md:items-center mb-4 md:mb-0">
                    {/* Fixed toggle switch container */}
                    <div className="flex items-center">
                      {/* Custom styled checkbox with contained toggle */}
                      <div 
                        onClick={handleTermsToggle}
                        className={`relative w-10 h-5 md:w-12 md:h-6 flex items-center rounded-full p-1 cursor-pointer ${formData.termsAgreed ? 'bg-green-700' : 'bg-gray-300'} ${formErrors.termsAgreed && showErrors ? 'ring-2 ring-red-500' : ''}`}
                      >
                        {/* Toggle Circle - ensure it stays within background */}
                        <div 
                          className={`bg-white w-4 h-4 md:w-5 md:h-5 rounded-full shadow-md transform duration-300 ease-in-out ${formData.termsAgreed ? 'translate-x-5 md:translate-x-5' : 'translate-x-0'}`}
                        ></div>
                      </div>
                    </div>
                    <label className="text-xs sm:text-sm md:text-base ml-3 cursor-pointer" onClick={handleTermsToggle}>
                      {t('terms_agreement')}
                    </label>
                  </div>
                  {formErrors.termsAgreed && showErrors && (
                    <p className="text-red-500 text-xs md:text-sm mb-4 md:mb-0">{formErrors.termsAgreed}</p>
                  )}
                  
                  <button
                    onClick={handleSubmit}
                    onMouseEnter={() => setSendButtonHovered(true)}
                    onMouseLeave={() => setSendButtonHovered(false)}
                    className="relative overflow-hidden"
                    disabled={isSubmitting}
                  >
                    {/* The text content that stays the same size */}
                    <div className="flex items-center bg-transparent text-black px-4 sm:px-6 md:px-8 py-3 md:py-4 font-bold text-base md:text-xl relative z-10">
                      {isSubmitting ? t('sending') : t('send_it')}
                      {!isSubmitting && (
                        <svg className="w-4 h-4 md:w-6 md:h-6 ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                        </svg>
                      )}
                      {isSubmitting && (
                        <svg className="animate-spin ml-2 h-4 w-4 md:h-6 md:w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                      )}
                    </div>
                    
                    {/* The background that shrinks */}
                    <div 
                      className={`absolute inset-0 ${isSubmitting ? 'bg-gray-300' : 'bg-white'} transition-all duration-300`}
                      style={{
                        transform: (sendButtonHovered && !isSubmitting) ? 'scale(0.8)' : 'scale(1)',
                        transition: 'transform 0.3s ease'
                      }}
                    ></div>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Success and Error Modals */}
      {submissionStatus === 'success' && <ThankYouMessage />}
      {submissionStatus === 'error' && <ErrorMessage />}
    </>
  );
};

export default WorkTogether;